import { useMutation } from "@apollo/client";
import { gql } from "generated-graphql";
import { useCallback } from "react";

/**
 * Accept / reject a staged Scout data-change proposal on the server.
 *
 * These set the durable ScoutDataChange row's status (CONFIRMED / DECLINED),
 * which is now apply-authority: the follow-up execute_graphql apply reads the
 * row, not a regex scan of the synthetic `[scout-internal-event]` message. Oak
 * calls one of these BEFORE sending that synth message, so by the time the
 * Scout node runs the apply the row already authorizes it.
 */

const CONFIRM_DATA_CHANGE = gql(`
  mutation ConfirmScoutDataChange($input: ConfirmScoutDataChangeInput!) {
    confirmScoutDataChange(input: $input) {
      changeId
      status
    }
  }
`);

const DECLINE_DATA_CHANGE = gql(`
  mutation DeclineScoutDataChange($input: DeclineScoutDataChangeInput!) {
    declineScoutDataChange(input: $input) {
      changeId
      status
    }
  }
`);

export type ResolveScoutDataChangeInput = {
  changeId: string;
};

export function useScoutDataChangeMutations() {
  const [confirmMutation] = useMutation(CONFIRM_DATA_CHANGE);
  const [declineMutation] = useMutation(DECLINE_DATA_CHANGE);

  const confirmDataChange = useCallback(
    async (input: ResolveScoutDataChangeInput) => {
      await confirmMutation({ variables: { input } });
    },
    [confirmMutation]
  );

  const declineDataChange = useCallback(
    async (input: ResolveScoutDataChangeInput) => {
      await declineMutation({ variables: { input } });
    },
    [declineMutation]
  );

  return { confirmDataChange, declineDataChange };
}
